import { type ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { anonymizeLocation } from "./anonymizeLocation";
import { useTracking } from "./TrackingContext";

/*
  Titles on report pages have carried plate numbers in the past. Cheap to
  scrub, so we keep scrubbing regardless of what the current titles hold.
*/
const PLATE_PATTERN = /[A-Za-z]{1,2}\d{3,5}/i;

function anonymizeTitle(title: string) {
  return title.replace(PLATE_PATTERN, "{plate}");
}

export function TrackPageViews({ children }: { children: ReactNode }) {
  const location = useLocation();
  const tracking = useTracking();

  /*
    Guarding on the anonymized url rather than the raw pathname means neither
    a re-render nor a new tracking context identity can re-report a page view.
  */
  const reportedKey = useRef<string | null>(null);
  const reportedAppLoad = useRef(false);

  useEffect(() => {
    const path = anonymizeLocation(location) || "/";
    /*
      The guard key pairs the raw pathname with the anonymized result: raw
      alone would re-report when a dropped query param changes, anonymized
      alone would suppress a real visit when two different tokens mask to
      the same string.
    */
    const key = `${location.pathname}|${path}`;
    if (reportedKey.current === key) return;

    /*
      Deferred a tick because document.title is set by a child effect, and
      children's effects run before ours.
    */
    const timeout = window.setTimeout(() => {
      reportedKey.current = key;
      tracking.pageView({ path, title: anonymizeTitle(document.title) });
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [location, tracking]);

  useEffect(() => {
    if (reportedAppLoad.current) return;
    reportedAppLoad.current = true;
    tracking.appLoad();
  }, [tracking]);

  return <>{children}</>;
}
